import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from '../components/common/Toast';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const error = useCallback((message: string) => showToast(message, 'error'), [showToast]);
  const info = useCallback((message: string) => showToast(message, 'info'), [showToast]);
  const warning = useCallback((message: string) => showToast(message, 'warning'), [showToast]);

  // Auto-initialize global toast function
  React.useEffect(() => {
    setGlobalToast(showToast);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, info, warning }}>
      {children}
      <div className="fixed top-2 left-2 right-2 lg:top-8 lg:left-auto lg:right-8 z-50 space-y-3 lg:space-y-4 pointer-events-none">
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className="pointer-events-auto"
            style={{
              transform: `translateY(${index * 90}px)`,
              transition: 'transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              animationDelay: `${index * 150}ms`
            }}
          >
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

// Global toast function for plug-and-play usage
let globalToastFunction: ((message: string, type?: 'success' | 'error' | 'info' | 'warning') => void) | null = null;

export function setGlobalToast(fn: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void) {
  globalToastFunction = fn;
}

// Simple plug-and-play toast function
export function toast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') {
  if (globalToastFunction) {
    globalToastFunction(message, type);
  } else {
    console.warn('Toast not initialized. Wrap your app with ToastProvider.');
    alert(message); // Fallback to alert if toast not initialized
  }
}

// Convenience methods
toast.success = (message: string) => toast(message, 'success');
toast.error = (message: string) => toast(message, 'error');
toast.info = (message: string) => toast(message, 'info');
toast.warning = (message: string) => toast(message, 'warning');
